import { useTranslation } from 'react-i18next';
import { Rocket } from 'lucide-react';
import { SegmentedControl } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/components/basic/EmptyState';
import { LaunchCard } from './LaunchCard';
import { LaunchKpiPanel } from './LaunchKpiPanel';
import type { Launch, LaunchStatus } from '../types';

interface LaunchesListViewProps {
  launches: Launch[];
  filter: LaunchStatus | 'all';
  onFilterChange: (f: LaunchStatus | 'all') => void;
  onOpenLaunch: (launchId: string) => void;
  onPublishLaunch?: (launchId: string) => void;
  onCloseLaunch?: (launchId: string) => void;
  onCreateLaunch?: () => void;
  hasActiveQuery?: boolean;
  hasAnyLaunches?: boolean;
  canManage?: boolean;
  kpis: { activeLaunches: number; totalAvailable: number; totalSold: number; totalUnits: number };
}

/**
 * List view with KPI cards, status filter pills, and launch card grid.
 */
export function LaunchesListView({ launches, filter, onFilterChange, onOpenLaunch, onPublishLaunch, onCloseLaunch, onCreateLaunch, hasActiveQuery, hasAnyLaunches = true, canManage, kpis }: LaunchesListViewProps) {
  const { t } = useTranslation('features/launches');

  // A broker cannot act on a draft and cannot see one in the list, so the
  // filter that selects them is theirs to not have.
  const filters: Array<{ value: LaunchStatus | 'all'; label: string }> = [
    { value: 'open', label: t('list.filters.open') },
    ...(canManage ? [{ value: 'draft' as const, label: t('list.filters.draft') }] : []),
    { value: 'closed', label: t('list.filters.closed') },
    { value: 'all', label: t('list.filterAll') },
  ];

  return (
    <div className="flex flex-col gap-[var(--space-5)]">
      {hasAnyLaunches && (
        <>
          {canManage && <LaunchKpiPanel kpis={kpis} />}

          <SegmentedControl
            value={filter}
            onChange={onFilterChange}
            items={filters}
            variant="pill"
            size="sm"
            ariaLabel={t('list.filterAriaLabel')}
            testId="launches-status-filter"
          />
        </>
      )}

      {launches.length === 0 ? (
        <EmptyState
          icon={
            <Rocket width={28} height={28} style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />
          }
          title={hasActiveQuery ? t('list.noResults.title') : t('list.empty.title')}
          description={hasActiveQuery ? t('list.noResults.description') : t('list.empty.description')}
          action={
            !hasActiveQuery && canManage && onCreateLaunch
              ? { label: t('list.empty.add'), onClick: onCreateLaunch }
              : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-[var(--space-4)] lg:grid-cols-2">
          {launches.map((launch) => (
            <LaunchCard
              key={launch.launch_id}
              launch={launch}
              onClick={() => onOpenLaunch(launch.launch_id)}
              canManage={canManage}
              onPublish={onPublishLaunch ? () => onPublishLaunch(launch.launch_id) : undefined}
              onClose={onCloseLaunch ? () => onCloseLaunch(launch.launch_id) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
